import React, { useState, useEffect } from 'react';
import {
    Breadcrumb, Layout, Spin, Tag, Typography, Table, Space, Button, message, Modal, Descriptions, Row, Col,
    Card // Import Card component
    , Input
} from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import { APPBACKGROUND_COLOR, MAINAPIURL } from '../appsettings';
import { AlertError, AlertSuccess } from '../usableclasses';
import { useRef } from 'react';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { useNavigate } from 'react-router-dom';
// Removed PDFViewer and PaymentSlipDocument imports

const { Content } = Layout;
const { Text } = Typography;
// Removed Option import

const TellerNeedToIssue = () => { // Renamed component
    const ApiURL = MAINAPIURL;
    const navigator = useNavigate();

    const TOKEN = localStorage.getItem("AUTHCODE");
    const USERID = localStorage.getItem("USERID"); // USERID might not be directly used here, but kept for consistency

    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);

    const [isIssueModalVisible, setIsIssueModalVisible] = useState(false);
    const [selectedOrderForIssue, setSelectedOrderForIssue] = useState(null);
    const [tableFilters, setTableFilters] = useState({}); // State to hold table filters
    // State for table search
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [issuing, setIssuing] = useState(false);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const headers = { SystemAccessToken: TOKEN };
            // Placeholder API endpoint for "Need to Issue" orders
            const response = await axios.get(`${ApiURL}/warehouse/allordersforissuing`, { headers });
            setOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching orders for issue:', error);
            AlertError('Failed to fetch orders for issue. Please try again later.'); // Updated error message
        } finally {
            setLoading(false);
        }
    };

    const getStatusTag = (status) => {
        const statusUpper = (status || '').toUpperCase();
        let color = 'default';
        switch (statusUpper) {
            case 'SUBMITTED': color = 'cyan'; break;
            case 'UNPAID': color = 'orange'; break;
            case 'PAYMENT FAILED': color = 'volcano'; break;
            case 'PAID': color = 'green'; break;
            case 'PROCESSING': color = 'blue'; break;
            case 'ORDER READY': color = 'purple'; break;
            case 'COMPLETED': color = 'geekblue'; break;
            case 'CANCELLED': color = 'red'; break;
            default: break;
        }
        return <Tag color={color}>{statusUpper || 'N/A'}</Tag>;
    };

    const openIssueModal = (order) => {
        setSelectedOrderForIssue(order);
        setIsIssueModalVisible(true);
    };

    const handleIssueOrder = async () => {
        if (!selectedOrderForIssue) return;

        try {
            setIssuing(true);
            const payload = {
                orderId: selectedOrderForIssue.orderId.toString(), // Ensure it's a string as per payload
                issueingBy: USERID,
            };

            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.post(`${ApiURL}/warehouse/orderissuetocustomer`, payload, { headers });

            if (res.data === true) {
                AlertSuccess("Order successfully issued to customer!");
                setIsIssueModalVisible(false);
                fetchOrders(); // Refresh the list
            } else {
                AlertError("Failed to issue order. Please try again.");
                console.error("Order issue failed:", res.data);
            }
        } catch (error) {
            console.error("Error issuing order:", error);
            let errorMessage = "Failed to issue order. Please try again.";
            if (error.response && error.response.data && error.response.data.message) {
                errorMessage = error.response.data.message;
            }
            AlertError(errorMessage);
        } finally {
            setIssuing(false);
        }
    };

    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex, closeDropdown = true) => {
        confirm({ closeDropdown });
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setTableFilters(prev => ({
            ...prev,
            [dataIndex]: selectedKeys[0] ? [selectedKeys[0]] : null // Store as array for consistency with AntD filters
        }));
    };

    const getColumnSearchProps = (dataIndex, title) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${title}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters(); // Clears internal AntD filter state for this column
                            setSearchText(''); // Clear local search text state
                            setSearchedColumn(''); // Clear local searched column state
                            setTableFilters(prev => { // Update the global tableFilters state for this column
                                const newFilters = { ...prev };
                                delete newFilters[dataIndex];
                                return newFilters;
                            });
                            confirm({ closeDropdown: true }); // Close dropdown and apply changes
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button type="link" size="small" onClick={() => { close(); }}>
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
        onFilterDropdownOpenChange: (visible) => { if (visible) { setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (<Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />) : (text),
        filteredValue: tableFilters[dataIndex] || null, // Control filter value
    });

    // Columns for the table
    const columns = [
        // Removed the 'Action' column with the "Payment" button
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        variant="outlined"
                        size="small"
                        color='yellow'
                        onClick={() => openIssueModal(record)}
                    >
                        Issue
                    </Button>
                    <Button
                        type='primary'
                        variant='solid'
                        size='small'
                        onClick={() => {
                            navigator(`/orders/view/${record.orderId}`); // Navigate to order view
                        }}
                    >
                        View
                    </Button>
                </Space>
            ),
        },
        { title: 'Order No', dataIndex: 'orderRef', key: 'orderRef', ...getColumnSearchProps('orderRef', 'Order No') },
        { title: 'Consultant ID', dataIndex: 'consultantId', key: 'consultantId', ...getColumnSearchProps('consultantId', 'Consultant ID') },
        { title: 'First Name', dataIndex: 'firstName', key: 'firstName', ...getColumnSearchProps('firstName', 'First Name') },
        { title: 'Last Name', dataIndex: 'lastName', key: 'lastName', ...getColumnSearchProps('lastName', 'Last Name') },
        {
            title: 'Order Date/Time',
            dataIndex: 'orderDateTime',
            key: 'orderDateTime',
            render: (text) => text ? new Date(text).toLocaleString() : 'N/A'
        },
        {
            title: 'Platform',
            dataIndex: 'orderPlatform',
            key: 'orderPlatform',
            render: (text) => text?.toUpperCase() || 'N/A',
            ...getColumnSearchProps('orderPlatform', 'Platform')
        },
        {
            title: 'Order Total',
            dataIndex: 'orderCost',
            key: 'orderCost',
            render: (text) => parseFloat(text || 0).toFixed(2),
            ...getColumnSearchProps('orderCost', 'Order Total')
        },
        { title: 'Ship Method', dataIndex: 'shippedMethod', key: 'shippedMethod', ...getColumnSearchProps('shippedMethod', 'Ship Method') },
        {
            title: 'Is Paid',
            dataIndex: 'isPaid',
            key: 'isPaid',
            render: (isPaid) => getStatusTag(isPaid ? 'PAID' : 'UNPAID'),
            filters: [
                { text: 'Paid', value: true },
                { text: 'Unpaid', value: false },
            ],
            onFilter: (value, record) => record.isPaid === value,
        },
        {
            title: 'Order Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            render: (text) => getStatusTag(text),
            filters: [
                { text: 'Submitted', value: 'Submitted' },
                { text: 'Processing', value: 'Processing' },
                { text: 'Order Ready', value: 'Order Ready' },
                { text: 'Completed', value: 'Completed' },
                { text: 'Cancelled', value: 'Cancelled' },
            ],
            onFilter: (value, record) => record.orderStatus === value,
        },
    ];

    const handleTableChange = (pagination, filters, sorter) => {
        setTableFilters(filters);
        // If you have server-side pagination/sorting/filtering, you would trigger data fetch here
    };

    const handleClearAllTableFilters = () => {
        setTableFilters({}); // Clear all filters
        setSearchText(''); // Clear any active search text
        setSearchedColumn(''); // Clear any active searched column
    };

    return (
        <Spin spinning={loading} tip="Loading...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item>Teller</Breadcrumb.Item>
                        <Breadcrumb.Item>Need to Issuing</Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]} justify="space-between" align="middle">
                            <Col xs={24} sm={12} md={12}>
                                <Space wrap>
                                    <Tag color="blue">Total Orders to Issue: {orders.length}</Tag>
                                </Space>
                            </Col>
                            <Col xs={24} sm={12} md={12} style={{ textAlign: 'right' }}>
                                <Space wrap>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClearAllTableFilters}
                                        style={{ marginLeft: 8 }}
                                    >
                                        Clear All Table Filters
                                    </Button>
                                    <Button onClick={fetchOrders}>Refresh List</Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    <Table
                        dataSource={orders}
                        columns={columns}
                        rowKey="orderId"
                        scroll={{ x: 'max-content' }} // Make table horizontally scrollable
                        pagination={{
                            pageSize: 10, // Default page size
                            showSizeChanger: false, // Hide page size changer
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        onChange={handleTableChange} // Add onChange handler
                        locale={{
                            emptyText: loading ? 'Fetching orders...' : 'No orders currently need to be issued.' // Updated empty text
                        }}
                    />

                    <Modal
                        title="Issue Order to Customer"
                        open={isIssueModalVisible}
                        onCancel={() => setIsIssueModalVisible(false)}
                        footer={[
                            <Button key="back" onClick={() => setIsIssueModalVisible(false)} danger>
                                Cancel
                            </Button>,
                            <Button
                                key="submit"
                                type="primary"
                                loading={issuing}
                                onClick={handleIssueOrder}
                            >
                                Confirm Issue
                            </Button>,
                        ]}
                    >
                        {selectedOrderForIssue && (
                            <>
                                <Descriptions title="Order Details" bordered column={2} size="small">
                                    <Descriptions.Item label="Order No">{selectedOrderForIssue.orderRef}</Descriptions.Item>
                                    <Descriptions.Item label="Consultant ID">{selectedOrderForIssue.consultantId}</Descriptions.Item>
                                    <Descriptions.Item label="First Name">{selectedOrderForIssue.firstName}</Descriptions.Item>
                                    <Descriptions.Item label="Last Name">{selectedOrderForIssue.lastName}</Descriptions.Item>
                                    <Descriptions.Item label="Mobile No">{selectedOrderForIssue.mobileNumber}</Descriptions.Item>
                                    <Descriptions.Item label="Order Date">{new Date(selectedOrderForIssue.orderDate).toLocaleDateString()}</Descriptions.Item>
                                    <Descriptions.Item label="Order Date/Time">{new Date(selectedOrderForIssue.orderDateTime).toLocaleString()}</Descriptions.Item>
                                    <Descriptions.Item label="Order Status">{selectedOrderForIssue.orderStatus}</Descriptions.Item>
                                    <Descriptions.Item label="Order Platform">{selectedOrderForIssue.orderPlatform}</Descriptions.Item>
                                    <Descriptions.Item label="Is Paid">{selectedOrderForIssue.isPaid ? 'Yes' : 'No'}</Descriptions.Item>
                                    <Descriptions.Item label="Is Canceled">{selectedOrderForIssue.isCanceled ? 'Yes' : 'No'}</Descriptions.Item>
                                    <Descriptions.Item label="Order Cost">{parseFloat(selectedOrderForIssue.orderCost).toFixed(2)}</Descriptions.Item>
                                    <Descriptions.Item label="Ship Method">{selectedOrderForIssue.shippedMethod}</Descriptions.Item>
                                </Descriptions>
                                <p style={{ marginTop: 20 }}>
                                    Are you sure you want to issue this order to the customer?
                                </p>
                            </>
                        )}
                    </Modal>

                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default TellerNeedToIssue; // Renamed export